import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Form, Select, DatePicker, Button, message, Table } from "antd";
import { saveAs } from "file-saver";
import * as Papa from "papaparse";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../features/cutomers/customerSlice";
import { getAttendance } from "../features/attendance/attendanceSlice";
import { getStates } from "../features/statecity/statecitySlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOn, faToggleOff } from "@fortawesome/free-solid-svg-icons";
import SkeletonLoader from "../components/SkeletonLoader/Index";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AttendanceLayout = () => {
  const [isPercentageView, setIsPercentageView] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [filteredAllVcVt, setFilteredAllVcVt] = useState([]);
  const [vcData, setVcData] = useState([]);
  const [vtData, setVtData] = useState([]);
  const [vtName, setVtName] = useState("");
  const [vcName, setVcName] = useState("");
  const [selectedVC, setSelectedVC] = useState("");
  const [selectedVT, setSelectedVT] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: "Present (Days)",
        data: [],
        backgroundColor: "#4caf50",
      },
      {
        label: "Absent (Days)",
        data: [],
        backgroundColor: "#f44336",
      },
    ],
  });

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const attendanceState = useSelector(
    (state) => state.attendance.attendancesall
  );
  const statelistState = useSelector((state) => state.statecity.statelist);
  const vclistState = useSelector((state) => state.customer.customers);

  useEffect(() => {
    // Fetch initial data on page load
    setLoading(true);
    dispatch(getStates());
    dispatch(getUsers());
    dispatch(getAttendance("")).then(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (attendanceState?.data) {
      console.log("attendanceState:", attendanceState);
      handleInitialData(attendanceState.data);
    }
  }, [attendanceState]);

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedDistrict("");
    setSelectedVC("");
    setSelectedVT("");
    setVcData([]);
    setVtData([]);
    setLoading(true);

    try {
      const stateFilterData = statelistState.filter(
        (district) => district.state === value
      );
      setDistrictOptions(stateFilterData);

      const filteredVCData = vclistState.filter(
        (customer) => customer.state === value && customer.role === "vc"
      );
      setVcData(filteredVCData);
    } catch (error) {
      message.error("Failed to fetch districts or VCs.");
    } finally {
      setLoading(false);
    }
  };

  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedVC("");
    setSelectedVT("");
    setVtData([]);

    const filteredVCData = vclistState.filter(
      (customer) =>
        customer.district === value &&
        customer.role === "vc" &&
        customer.state === selectedState
    );
    setVcData(filteredVCData);
  };

  const handleVcChange = (value, option) => {
    setSelectedVC(value);
    setSelectedVT("");
    setVcName(option.name);

    const filteredVTData = vclistState.filter(
      (customer) => customer.vcuser_id === value && customer.role === "vt"
    );
    setVtData(filteredVTData);
  };

  const handleVTChange = (value, option) => {
    setSelectedVT(value);
    setVtName(option.name);
  };

  // Process data for graph and table based on API response format
  const processData = (data, isPercentageView) => {
    let filteredData = [];
    let labels = [];
    let presentData = [];
    let absentData = [];
    let tableData = [];

    // Combine present and absent data for VCs and VTs
    const vcPresent = data?.vcData?.[0]?.present || [];
    const vcAbsent = data?.vcData?.[0]?.absent || [];
    const vtPresent = data?.vcData?.[1]?.present || [];
    const vtAbsent = data?.vcData?.[1]?.absent || [];
    const allData = [...vcPresent, ...vcAbsent, ...vtPresent, ...vtAbsent];

    // Case 1: No filters -> Show state-wise data
    if (!selectedState && !selectedDistrict && !selectedVC && !selectedVT) {
      const stateMap = {};
      allData.forEach((item) => {
        const state = item.state || "Unknown";
        if (!stateMap[state]) {
          stateMap[state] = { present: 0, absent: 0 };
        }
        stateMap[state].present += item.present_count || 0;
        stateMap[state].absent += item.absent_count || 0;
      });

      labels = Object.keys(stateMap);
      presentData = labels.map(
        (state) =>
          (isPercentageView
            ? (stateMap[state].present /
                (stateMap[state].present + stateMap[state].absent)) *
              100
            : stateMap[state].present) || 0
      );
      absentData = labels.map(
        (state) =>
          (isPercentageView
            ? (stateMap[state].absent /
                (stateMap[state].present + stateMap[state].absent)) *
              100
            : stateMap[state].absent) || 0
      );

      tableData = labels.map((state) => ({
        name: statelistState.find((s) => s.code === state)?.state_name || state,
        role: "State",
        state: state,
        district: "-",
        present_count: stateMap[state].present,
        absent_count: stateMap[state].absent,
        attendancePercentage:
          (stateMap[state].present /
            (stateMap[state].present + stateMap[state].absent)) *
            100 || 0,
      }));
    }

    // Case 2: Only State is selected -> Show all districts in that state
    else if (selectedState && !selectedDistrict && !selectedVC && !selectedVT) {
      const districtMap = {};
      const filteredByState = allData.filter(
        (item) => item.state === selectedState
      );

      filteredByState.forEach((item) => {
        const district = item.district || "Unknown";
        if (!districtMap[district]) {
          districtMap[district] = { present: 0, absent: 0 };
        }
        districtMap[district].present += item.present_count || 0;
        districtMap[district].absent += item.absent_count || 0;
      });

      labels = Object.keys(districtMap);
      presentData = labels.map(
        (district) =>
          (isPercentageView
            ? (districtMap[district].present /
                (districtMap[district].present +
                  districtMap[district].absent)) *
              100
            : districtMap[district].present) || 0
      );
      absentData = labels.map(
        (district) =>
          (isPercentageView
            ? (districtMap[district].absent /
                (districtMap[district].present +
                  districtMap[district].absent)) *
              100
            : districtMap[district].absent) || 0
      );

      tableData = labels.map((district) => ({
        name: district,
        role: "District",
        state: selectedState,
        district: district,
        present_count: districtMap[district].present,
        absent_count: districtMap[district].absent,
        attendancePercentage:
          (districtMap[district].present /
            (districtMap[district].present + districtMap[district].absent)) *
            100 || 0,
      }));
    }

    // Case 3: State and District selected -> Show all VCs in the district
    else if (selectedState && selectedDistrict && !selectedVC && !selectedVT) {
      filteredData = [...vcPresent, ...vcAbsent].filter(
        (item) =>
          item.state === selectedState &&
          item.district === selectedDistrict &&
          item.role === "vc"
      );

      tableData = filteredData.map((item) => {
        const presentCount = item.present_count || 0;
        const absentCount = item.absent_count || 0;
        const total = presentCount + absentCount;
        return {
          name: item.name,
          role: item.role,
          state: item.state,
          district: item.district,
          present_count: presentCount,
          absent_count: absentCount,
          attendancePercentage: total > 0 ? (presentCount / total) * 100 : 0,
        };
      });

      labels = tableData.map((item) => item.name);
      presentData = tableData.map((item) =>
        isPercentageView ? item.attendancePercentage : item.present_count
      );
      absentData = tableData.map((item) =>
        isPercentageView ? 100 - item.attendancePercentage : item.absent_count
      );
    }

    // Case 4: State, District, and VC selected -> Show VTs under the VC
    else if (selectedState && selectedDistrict && selectedVC && !selectedVT) {
      filteredData = [...vtPresent, ...vtAbsent].filter(
        (item) =>
          item.state === selectedState &&
          item.district === selectedDistrict &&
          item.role === "vt" &&
          vclistState.find((vt) => vt._id === item.user_id)?.vcuser_id ===
            selectedVC
      );

      tableData = filteredData.map((item) => {
        const presentCount = item.present_count || 0;
        const absentCount = item.absent_count || 0;
        const total = presentCount + absentCount;
        return {
          name: item.name,
          role: item.role,
          state: item.state,
          district: item.district,
          present_count: presentCount,
          absent_count: absentCount,
          attendancePercentage: total > 0 ? (presentCount / total) * 100 : 0,
        };
      });

      labels = tableData.map((item) => item.name);
      presentData = tableData.map((item) =>
        isPercentageView ? item.attendancePercentage : item.present_count
      );
      absentData = tableData.map((item) =>
        isPercentageView ? 100 - item.attendancePercentage : item.absent_count
      );
    }

    // Case 5: All filters (State, District, VC, VT) -> Show single VT
    else if (selectedState && selectedDistrict && selectedVC && selectedVT) {
      filteredData = [...vtPresent, ...vtAbsent].filter(
        (item) =>
          item.state === selectedState &&
          item.district === selectedDistrict &&
          item.role === "vt" &&
          item.name === vtName &&
          vclistState.find((vt) => vt._id === item.user_id)?.vcuser_id ===
            selectedVC
      );

      tableData = filteredData.map((item) => {
        const presentCount = item.present_count || 0;
        const absentCount = item.absent_count || 0;
        const total = presentCount + absentCount;
        return {
          name: item.name,
          role: item.role,
          state: item.state,
          district: item.district,
          present_count: presentCount,
          absent_count: absentCount,
          attendancePercentage: total > 0 ? (presentCount / total) * 100 : 0,
        };
      });

      labels = tableData.map((item) => item.name);
      presentData = tableData.map((item) =>
        isPercentageView ? item.attendancePercentage : item.present_count
      );
      absentData = tableData.map((item) =>
        isPercentageView ? 100 - item.attendancePercentage : item.absent_count
      );
    }

    setFilteredAllVcVt(tableData);

    return {
      labels,
      datasets: [
        {
          label: isPercentageView
            ? "Attendance Percentage (Present)"
            : "Present (Days)",
          data: presentData,
          backgroundColor: "#4caf50",
        },
        {
          label: isPercentageView
            ? "Attendance Percentage (Absent)"
            : "Absent (Days)",
          data: absentData,
          backgroundColor: "#f44336",
        },
      ],
    };
  };

  const handleInitialData = (data) => {
    setLoading(true);
    const graphData = processData(data, isPercentageView);
    setGraphData(graphData);
    setLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await dispatch(
        getAttendance(
          `start_date=${selectedStartDate || ""}&end_date=${
            selectedEndDate || ""
          }&state=${selectedState || ""}&district=${
            selectedDistrict || ""
          }&vc_name=${vcName || ""}&vt_name=${vtName || ""}`
        )
      );
    } catch (error) {
      message.error("Failed to filter data.");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedVC("");
    setSelectedVT("");
    setVcData([]);
    setVtData([]);
    setVtName("");
    setVcName("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setFilteredAllVcVt([]);
    setGraphData({
      labels: [],
      datasets: [
        {
          label: "Present (Days)",
          data: [],
          backgroundColor: "#4caf50",
        },
        {
          label: "Absent (Days)",
          data: [],
          backgroundColor: "#f44336",
        },
      ],
    });
    dispatch(getAttendance(""));
  };

  const toggleView = () => {
    setIsPercentageView((prev) => !prev);
    const vcDatas = attendanceState?.data || {};
    const updatedGraphData = processData(vcDatas, !isPercentageView);
    setGraphData(updatedGraphData);
  };

  const exportToCSV = () => {
    const csvData = filteredAllVcVt.map((user) => ({
      Name: user.name,
      Role: user.role,
      State: user.state,
      District: user.district,
      Present: user.present_count,
      Absent: user.absent_count,
      AttendancePercentage: user.attendancePercentage,
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "attendance_data.csv");
  };

  const graphOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
    },
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => role?.toUpperCase(),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
    },
    {
      title: "Present",
      dataIndex: "present_count",
      key: "present_count",
    },
    {
      title: "Absent",
      dataIndex: "absent_count",
      key: "absent_count",
    },
    {
      title: "Attendance Percentage",
      dataIndex: "attendancePercentage",
      key: "attendancePercentage",
      render: (percentage) => `${percentage.toFixed(2)}%`,
    },
  ];

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h4 style={{ fontSize: "large", fontWeight: "bold" }}>
        Attendance Overview for VTs and VCs
      </h4>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "20px", marginLeft: "20px", gap: "10px" }}
      >
        <Form.Item name="state_name">
          <Select
            placeholder="Select State"
            onChange={handleStateChange}
            style={{ width: 150 }}
            value={selectedState}
            allowClear
          >
            {statelistState.map((i, j) => (
              <Select.Option key={j} value={i.code}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="district">
          <Select
            style={{ width: 160 }}
            placeholder="Select District"
            onChange={handleDistrictChange}
            loading={loading}
            value={selectedDistrict}
            allowClear
          >
            {districtOptions.flatMap((district) =>
              district.district_name.map((districtName, idx) => (
                <Select.Option key={idx} value={districtName}>
                  {districtName}
                </Select.Option>
              ))
            )}
          </Select>
        </Form.Item>

        <Form.Item name="vc_name">
          <Select
            placeholder="Select VC"
            onChange={handleVcChange}
            style={{ width: 150 }}
            value={selectedVC}
            disabled={!selectedState}
            allowClear
          >
            {vcData.map((item) => (
              <Select.Option key={item._id} value={item._id} name={item.name}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="vt_name">
          <Select
            placeholder="Select VT"
            onChange={handleVTChange}
            style={{ width: 150 }}
            value={selectedVT}
            disabled={!selectedVC}
            allowClear
          >
            {vtData.map((item) => (
              <Select.Option key={item._id} value={item._id} name={item.name}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="start_date">
          <DatePicker
            placeholder="From"
            style={{ width: 50 }}
            onChange={(value) => {
              setSelectedStartDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>

        <Form.Item name="end_date">
          <DatePicker
            placeholder="To"
            style={{ width: 50 }}
            onChange={(value) => {
              setSelectedEndDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>

        <Form.Item>
          <div
            onClick={toggleView}
            style={{
              cursor: "pointer",
              marginTop: 16,
              marginLeft: 15,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontWeight: "700" }}>{"Count"}</span>
            <FontAwesomeIcon
              icon={isPercentageView ? faToggleOn : faToggleOff}
              style={{
                color: isPercentageView ? "#6c757d" : "#94938f",
                fontSize: "30px",
              }}
            />
            <span style={{ fontWeight: "700" }}>{"Percentage"}</span>
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            style={{
              backgroundColor: "#28a745",
              color: "#fff",
              marginTop: 16,
              marginLeft: 20,
            }}
            htmlType="submit"
          >
            Apply Filter
          </Button>
        </Form.Item>

        <Form.Item>
          <Button
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              marginTop: 16,
              marginLeft: -12,
            }}
            onClick={resetFilters}
          >
            Reset Filter
          </Button>
        </Form.Item>
      </Form>

      {!loading ? (
        <div
          style={{
            marginTop: "20px",
            padding: "10px",
            width: "100%",
            height: "300px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#DCDCDC",
            borderRadius: "8px",
          }}
        >
          {graphData.labels.length > 0 ? (
            <Bar data={graphData} options={graphOptions} />
          ) : (
            <p>No data available for the selected filters.</p>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 15 }}>
          <SkeletonLoader count={1} height={300} />
        </div>
      )}

      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "20px",
        }}
      >
        <Table
          columns={tableColumns}
          dataSource={filteredAllVcVt}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: filteredAllVcVt.length,
            onChange: (page) => setCurrentPage(page),
          }}
          rowKey={(record) => record.name + record.role}
        />
      </div>

      <div style={{ margin: "20px 0 0 20px", display: "none", gap: "35px" }}>
        <Button
          style={{
            padding: "8px 12px",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            backgroundColor: "#28a745",
            borderRadius: "8px",
            marginRight: "12rem",
          }}
          onClick={exportToCSV}
        >
          Export as CSV
        </Button>
      </div>
    </div>
  );
};

const graphOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: "top",
    },
  },
};

const tableColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Role",
    dataIndex: "role",
    key: "role",
    render: (role) => role?.toUpperCase(),
  },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
  },
  {
    title: "District",
    dataIndex: "district",
    key: "district",
  },
  {
    title: "Present",
    dataIndex: "present_count",
    key: "present_count",
  },
  {
    title: "Absent",
    dataIndex: "absent_count",
    key: "absent_count",
  },
  {
    title: "Attendance Percentage",
    dataIndex: "attendancePercentage",
    key: "attendancePercentage",
    render: (percentage) => `${percentage.toFixed(2)}%`,
  },
];

export default AttendanceLayout;
